import React from "react";
import { useSelector } from "@xstate/react";
import { narrativeManager } from "@engine/narrative/NarrativeManager.js";
import { resolvePortraitSrc } from "@engine/narrative/portraits.js";
import { PartyStore } from "@store/PartyStore.js";
import type { PlayerCharacter } from "@engine/types/entity.js";
import type { DialogueTone } from "@engine/types/narrative.js";

const TONE_COLOR: Record<DialogueTone, string> = {
  neutral: "#e8e8e8",
  warm: "#f5a623",
  hostile: "#e05555",
};

/**
 * Dialogue overlay rendered during Ink narrative sequences.
 *
 * Subscribes directly to `narrativeManager.narrativeActor` so it updates
 * reactively whenever the machine delivers a new step.
 *
 * States handled:
 *  - "selectingSpeaker": portrait row for picking the active speaker (N03)
 *  - "displayingText": narrative text + choices or continue button
 */
export function DialogueBox() {
  const snapshot = useSelector(narrativeManager.narrativeActor, (s) => s);
  const step = snapshot.context.currentStep;
  const portrait = snapshot.context.portrait;
  const speakerId = snapshot.context.speakerId;

  if (snapshot.matches("inactive")) return null;

  // ── Speaker selection overlay (N03) ──────────────────────────────────────
  if (snapshot.matches("selectingSpeaker")) {
    return <SpeakerSelector />;
  }

  if (!step) return null;

  const hasChoices = step.choices.length > 0;
  const speakerName = speakerId ? PartyStore.getMember(speakerId)?.name : null;

  return (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 500,
        maxWidth: "92vw",
        // Cap the panel so long intro scripts scroll inside it instead of
        // growing past the viewport edge (svh keeps the cap honest under
        // mobile browser chrome; unsupported browsers just skip the cap).
        maxHeight: "min(78svh, 640px)",
        display: "flex",
        flexDirection: "column",
        background: "rgba(10, 10, 20, 0.92)",
        color: "#e8e8e8",
        padding: "20px 24px",
        borderRadius: 4,
        border: "1px solid #445",
        boxShadow: "0 4px 24px rgba(0,0,0,0.7)",
        pointerEvents: "auto",
        zIndex: 100,
      }}
    >
      {/* NPC portrait header — shown when NPC has a portrait field */}
      {portrait && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 14,
            paddingBottom: 12,
            borderBottom: "1px solid #334",
          }}
        >
          <img
            src={resolvePortraitSrc(portrait)}
            alt=""
            width={48}
            height={60}
            style={{
              border: "1px solid #556",
              borderRadius: 2,
              objectFit: "cover",
              background: "#111",
              flexShrink: 0,
            }}
          />
        </div>
      )}

      {/* Scroll region: byline + narrative text. Buttons stay pinned below
          so Continue/Close never leaves the screen on long passages. */}
      <div style={{ overflowY: "auto", minHeight: 0, flex: "1 1 auto" }}>
        {/* Speaker byline — shown when a party member is the active speaker (N03) */}
        {speakerName && (
          <p
            style={{
              margin: "0 0 8px",
              fontSize: 11,
              fontFamily: "monospace",
              color: "#8899bb",
              letterSpacing: 1,
              textTransform: "uppercase",
            }}
          >
            {speakerName} speaks
          </p>
        )}

        {/* Narrative text — color driven by Ink `# tone:` tag */}
        <p
          style={{
            margin: "0 0 16px",
            lineHeight: 1.65,
            fontSize: 14,
            whiteSpace: "pre-wrap",
            color: TONE_COLOR[step.tone],
            transition: "color 0.2s ease",
          }}
        >
          {step.text}
        </p>
      </div>

      {/* Choices or Continue/Close — pinned below the scroll region */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
        {hasChoices ? (
          step.choices.map((c) => (
            <button
              key={c.index}
              onClick={() => narrativeManager.selectChoice(c.index)}
              style={{
                background: "#1e2a3a",
                color: "#cce",
                border: "1px solid #446",
                padding: "7px 14px",
                cursor: "pointer",
                textAlign: "left",
                fontSize: 13,
                borderRadius: 2,
              }}
            >
              › {c.text}
            </button>
          ))
        ) : (
          <button
            onClick={() => narrativeManager.close()}
            style={{
              alignSelf: "flex-end",
              background: "#334",
              color: "#cce",
              border: "1px solid #557",
              padding: "6px 20px",
              cursor: "pointer",
              fontSize: 13,
              borderRadius: 2,
            }}
          >
            {step.ended ? "Close" : "Continue"}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Speaker selector overlay (N03) ───────────────────────────────────────────

function SpeakerSelector() {
  const members = PartyStore.getMembers();

  return (
    <div
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 500,
        maxWidth: "92vw",
        maxHeight: "min(78svh, 640px)",
        overflowY: "auto",
        background: "rgba(10, 10, 20, 0.92)",
        color: "#e8e8e8",
        padding: "20px 24px",
        borderRadius: 4,
        border: "1px solid #445",
        boxShadow: "0 4px 24px rgba(0,0,0,0.7)",
        pointerEvents: "auto",
        zIndex: 100,
      }}
    >
      <p
        style={{
          margin: "0 0 14px",
          fontSize: 12,
          fontFamily: "monospace",
          color: "#aab",
          letterSpacing: 1,
          textTransform: "uppercase",
        }}
      >
        Who speaks?
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {members.map((m) => (
          <SpeakerCard
            key={m.instanceId}
            member={m}
            onClick={() =>
              narrativeManager.narrativeActor.send({
                type: "SPEAKER_SELECTED",
                instanceId: m.instanceId,
              })
            }
          />
        ))}
      </div>
    </div>
  );
}

function SpeakerCard({
  member,
  onClick,
}: {
  member: PlayerCharacter;
  onClick: () => void;
}) {
  const portrait = (member as Partial<{ portrait: string | null }>).portrait ?? null;
  const initial = member.name.charAt(0).toUpperCase();

  return (
    <button
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        background: "#1a2030",
        border: "1px solid #446",
        borderRadius: 4,
        padding: "10px 14px",
        cursor: "pointer",
        color: "#cce",
        minWidth: 80,
      }}
    >
      {/* Avatar: portrait image if available, otherwise initial circle */}
      {portrait ? (
        <img
          src={resolvePortraitSrc(portrait)}
          alt=""
          width={44}
          height={44}
          style={{ borderRadius: 2, objectFit: "cover", background: "#111" }}
        />
      ) : (
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: "#2a3a5a",
            border: "1px solid #557",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            fontFamily: "monospace",
            color: "#aac",
          }}
        >
          {initial}
        </div>
      )}
      <span style={{ fontSize: 12, fontFamily: "monospace" }}>{member.name}</span>
      <span style={{ fontSize: 10, color: "#778", fontFamily: "monospace" }}>
        {member.characterClass}
      </span>
    </button>
  );
}
